import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../lib/api';
import PublicHeader from '../components/PublicHeader';
import StarRating from '../components/StarRating';
import { useToast } from '../lib/toast';

const REASONS = {
  not_found: 'We could not find that booking.',
  email_mismatch: 'The email you entered does not match our records for this booking.',
  not_completed: 'Reviews are only available after your trip is completed.',
  already_reviewed: 'A review has already been submitted for this booking.',
};

export default function ReviewPage() {
  const { bid } = useParams();
  const [params] = useSearchParams();
  const { show } = useToast();
  const initialEmail = params.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [eligibility, setEligibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (initialEmail) check(initialEmail);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  async function check(em) {
    setChecking(true);
    try {
      const { data } = await api.get(`/bookings/${bid}/review-eligibility?email=${encodeURIComponent(em)}`);
      setEligibility(data);
    } catch (e) {
      setEligibility({ eligible: false, reason: 'not_found' });
    } finally {
      setChecking(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/reviews', {
        booking_id: bid,
        customer_email: email,
        rating,
        comment,
      });
      setDone(data);
      show('Thank you for your review!', 'success');
    } catch (err) {
      show(formatApiErrorDetail(err.response?.data?.detail) || 'Submit failed', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <PublicHeader />
      <div style={{ maxWidth: 560, margin: '60px auto', padding: 24 }}>
        {done ? (
          <div data-testid="review-thanks" className="card fade-up" style={{ padding: 36, textAlign: 'center' }}>
            <div style={{ fontSize: 56 }}>🌟</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: '#0D1B3E', marginTop: 12 }}>Thanks for your review!</h2>
            <div style={{ marginTop: 16 }}>
              <StarRating value={done.rating} size={28} />
            </div>
            {done.comment && (
              <p style={{ marginTop: 16, padding: 14, background: '#F1F5F9', borderRadius: 10, color: '#475569', fontSize: 14, fontStyle: 'italic' }}>
                &ldquo;{done.comment}&rdquo;
              </p>
            )}
            <Link to="/" className="btn-primary" style={{ marginTop: 22, display: 'inline-block', textDecoration: 'none' }}>
              Back to fleet
            </Link>
          </div>
        ) : !eligibility ? (
          <div className="card" style={{ padding: 32 }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-.02em' }}>Leave a review</h1>
            <p style={{ color: '#64748B', fontSize: 13, marginTop: 6 }}>
              Confirm the email used for booking <code style={{ background: '#F1F5F9', padding: '1px 6px', borderRadius: 4 }}>{bid.slice(-8).toUpperCase()}</code>.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (email) check(email);
              }}
              style={{ marginTop: 18 }}
            >
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>Email</label>
              <input
                data-testid="rev-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14 }}
              />
              <button data-testid="rev-check" type="submit" disabled={checking || !email} className="btn-primary" style={{ marginTop: 16, width: '100%', padding: 12 }}>
                {checking ? 'Checking…' : 'Continue'}
              </button>
            </form>
          </div>
        ) : !eligibility.eligible ? (
          <div data-testid="rev-blocked" className="card" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>🙅</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0F172A', marginTop: 12 }}>Can&apos;t leave a review</h2>
            <p style={{ color: '#64748B', fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
              {REASONS[eligibility.reason] || 'We were unable to verify this booking.'}
              {eligibility.reason === 'not_completed' && eligibility.current_status && (
                <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#94A3B8' }}>
                  Current booking status: <strong>{eligibility.current_status}</strong>
                </span>
              )}
            </p>
            <Link to="/" className="btn-ghost" style={{ marginTop: 22, display: 'inline-block', textDecoration: 'none' }}>
              Back to fleet
            </Link>
            {eligibility.reason === 'email_mismatch' && (
              <button
                onClick={() => setEligibility(null)}
                className="btn-primary"
                style={{ marginTop: 10, display: 'block', width: '100%' }}
              >
                Try a different email
              </button>
            )}
          </div>
        ) : (
          <form data-testid="rev-form" onSubmit={submit} className="card" style={{ padding: 32 }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-.02em' }}>How was your trip?</h1>
            <p style={{ color: '#64748B', fontSize: 14, marginTop: 6 }}>
              {eligibility.customer_name && <>Hi <strong>{eligibility.customer_name}</strong> — </>}
              tell other travelers how the <strong>{eligibility.vehicle_label}</strong> performed.
            </p>

            <div style={{ marginTop: 22, padding: 18, background: '#F8FAFC', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Your rating</div>
              <div style={{ marginTop: 8 }}>
                <StarRating value={rating} size={36} interactive onChange={setRating} />
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#64748B' }}>
                {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 20, marginBottom: 5 }}>
              Comment <span style={{ color: '#94A3B8', textTransform: 'none', fontWeight: 500 }}>(optional, max 1000 chars)</span>
            </label>
            <textarea
              data-testid="rev-comment"
              rows={5}
              maxLength={1000}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Smooth ride, great pickup process, the car was spotless…"
              style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
            />

            <button data-testid="rev-submit" type="submit" disabled={submitting} className="btn-red" style={{ marginTop: 18, width: '100%', padding: 14 }}>
              {submitting ? 'Submitting…' : 'Submit review'}
            </button>
            <p style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginTop: 10 }}>
              Reviews are public and show your first name & booking initial.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
